\
<template>
    <Card title="On Hold Queue">
        <b-form-row class="mb-2">
            <b-col>
                <SearchComponent label="Store" v-model="store" searchURL="/Store" prepopulate clearable />
            </b-col>
            <b-col>
                <SearchComponent label="Program" v-model="programFilter" search-u-r-l="/Program"
                    :params="{ active: true }" clearable prepopulate />
            </b-col>
            <b-col>
                <label>
                    SubStatus:
                </label>
                <v-select
                    :value="{ value: subStatus, label: onHoldSubStatuses.find(ss => ss.value == subStatus)?.label }"
                    :options="activeOnHoldSubStatuses" @input="subStatus = $event?.value" />
            </b-col>
            <b-col>
                <DateRangeFilter v-model="dateFilter" label="Date Range:" :clearable="false" />
            </b-col>
        </b-form-row>
        <b-row class="mb-2">
            <b-col class="d-flex justify-content-end">
                <b-button variant="info" size="sm" @click="$bvModal.show('onhold-column-customize-modal')">
                    <i class="fa fa-cog"></i> Customize Columns
                </b-button>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-table id="on-hold-table" ref="onHoldTable" api-url="Prescription/OnHold" :items="onHoldTableProvider"
                    :fields="visibleFields" :filter="tableFilter" responsive="xl" sort-icon-left show-empty
                    :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" no-local-sorting @row-clicked="selectRx">
                    <template #cell(actions)="data">
                        <div class="table-icon-actions">
                            <router-link :to="getPrescriptionDetailsRoute(data.item)" class="mr-1">
                                <b-icon icon="pencil-square" v-b-tooltip.hover title="Edit" />
                            </router-link>
                        </div>
                    </template>
                    <template #cell(rxId)="data">
                        <span v-b-tooltip.hover :title="`Memo: ${data.item.memo ?? ''}`">
                            {{ data.item.rxId }}
                        </span>
                    </template>
                </b-table>

                <BottomPagerBar v-model="pageNumber" :per-page="pageSize" :loading="loading"
                    :total-rows="totalItemCount" show-page-size-selector show-total item-name="prescription"
                    @change-page-size="pageSize = $event" />
            </b-col>
        </b-row>

        <!-- Column Customize Modal -->
        <b-modal id="onhold-column-customize-modal" title="Customize Table Columns" size="lg" @ok="saveColumnPreferences">
            <div class="mb-3">
                <h6>Select which columns to display in the on-hold queue table:</h6>
            </div>

            <div class="grid">
                <h6 style="grid-column: 1 / -1;" class="mt-3 mb-2">Core Columns</h6>
                <b-form-checkbox v-model="columnVisibility.productNameShort">Drug</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.patient">Patient</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.storeName">Store</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.rxId">Rx Id</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.quantity">Quantity</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.subStatus">Hold Status</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.nextTouchDate">Next Touch Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.fillDate">Fill Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.programName">Program</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.prescriberName">Doctor</b-form-checkbox>

                <h6 style="grid-column: 1 / -1;" class="mt-3 mb-2">Additional Columns</h6>
                <b-form-checkbox v-model="columnVisibility.ndc11">NDC</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.daySupply">Day Supply</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.memo">Memo</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.directions">Directions</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.writtenDate">Written Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.patientPay">Patient Pay</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.dispensed">Dispensed</b-form-checkbox>
            </div>

            <template #modal-footer="{ ok, cancel }">
                <b-button variant="secondary" @click="cancel()">Cancel</b-button>
                <b-button variant="primary" @click="ok()">Save Preferences</b-button>
            </template>
        </b-modal>
    </Card>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import SearchComponent from "@/components/SearchComponent.vue";
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import Axios, { AxiosRequestConfig } from "axios";
import { Prescription } from "@/models/Prescription";
import EnumSelector from '@/components/EnumSelector.vue';
import { BvTableCtxObject } from "bootstrap-vue";
import { Patient } from "@/models/Patient";
import moment from "moment";
import { DatePicker } from "element-ui";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import DateRangeFilter from "@/components/DateRangeFilter.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import TextUtils from "@/util/TextUtils";
import { getUserSetting, getUserSettingParsed, saveUserSetting } from "@/services/UserSettingsService";

@Component({
    components: {
        SearchComponent,
        EnumSelector,
        [DatePicker.name]: DatePicker,
        BottomPagerBar,
        DateRangeFilter
    }
})
export default class PrescriptionOnHoldQueue extends Vue {
    private store: Store = new Store();
    private programFilter: Program = new Program();
    private subStatus: number | null = null;

    private dateFilter: { startDate: any, endDate: any } = {
        startDate: moment().subtract(14, "days").toDate(),
        endDate: new Date()
    };

    private pageNumber = 1;
    private pageSize = 100;
    private loading = true;
    private totalItemCount = 200;

    private sortBy: string = "fillDate";
    private sortDesc: boolean = false;

    private columnVisibility: any = {
        // Core columns (visible by default)
        productNameShort: true,
        patient: true,
        storeName: true,
        rxId: true,
        quantity: true,
        subStatus: true,
        nextTouchDate: true,
        fillDate: true,
        programName: true,
        prescriberName: true,
        // Additional columns (hidden by default)
        ndc11: false,
        daySupply: false,
        memo: false,
        directions: false,
        writtenDate: false,
        patientPay: false,
        dispensed: false,
    };

    private skipFilterSave = false;

    mounted() {
        this.pageSize = getPageSizePreference(this, 'pageSize_OnHoldQueue', 100);
        this.restoreFilters();
        this.loadColumnPreferences();
        this.getOnHoldSubStatuses();
    }

    @Watch("pageSize")
    onPageSizeChanged() {
        savePageSizePreference(this, 'pageSize_OnHoldQueue', this.pageSize);
    }

    private restoreFilters() {
        const saved = getUserSettingParsed<any>('onHoldQueue_filters', null);
        if (!saved) return;
        this.skipFilterSave = true;
        if (saved.store?.id) this.store = new Store(saved.store);
        if (saved.program?.id) this.programFilter = new Program(saved.program);
        if (saved.subStatus !== undefined) this.subStatus = saved.subStatus;
        this.$nextTick(() => { this.skipFilterSave = false; });
    }

    private persistFilters() {
        if (this.skipFilterSave) return;
        saveUserSetting('onHoldQueue_filters', JSON.stringify({
            store: this.store?.id ? { id: this.store.id, name: this.store.name } : null,
            program: this.programFilter?.id ? { id: this.programFilter.id, name: this.programFilter.name } : null,
            subStatus: this.subStatus,
        }));
    }

    private get allFields() {
        return [
            { key: "productNameShort", label: "Drug", sortable: true },
            { key: "ndc11", label: "NDC", sortable: true },
            { key: "patient", label: "Patient", formatter: this.patientFormatter, sortable: true },
            { key: "prescriberName", label: "Doctor", sortable: true },
            { key: "storeName", label: "Store", sortable: true },
            { key: "rxId", label: "Rx Id", sortable: true },
            { key: "quantity", label: "Quantity" },
            { key: "daySupply", label: "Day Supply" },
            { key: "dispensed", label: "Dispensed" },
            { key: "subStatus", label: "Hold Status", formatter: this.subStatusLabel, sortable: true },
            { key: "nextTouchDate", label: "Next Touch Date", formatter: (d: Date) => d ? moment.utc(d).local().format("L LT") : "", sortable: true },
            { key: "fillDate", label: "Fill Date", formatter: (d: Date) => d ? moment.utc(d).format("L") : "", sortable: true },
            { key: "writtenDate", label: "Written Date", formatter: (d: Date) => d ? moment.utc(d).format("L") : "", sortable: true },
            { key: "programName", label: "Program", sortable: true },
            { key: "memo", label: "Memo" },
            { key: "directions", label: "Directions" },
            { key: "patientPay", label: "Patient Pay", formatter: (v: number | null) => v != null ? TextUtils.currencyFormatter(v) : "" },
            { key: "actions", label: "" },
        ];
    }

    get visibleFields() {
        return this.allFields.filter(f => f.key === "actions" || this.columnVisibility[f.key] !== false);
    }

    get tableFilter() {
        const filter: any = {
            pageNumber: this.pageNumber,
            pageSize: this.pageSize,
            fromDate: this.dateFilter.startDate,
            toDate: this.dateFilter.endDate,
            sortBy: this.sortBy ? this.sortBy.charAt(0).toUpperCase() + this.sortBy.slice(1) : this.sortBy,
            sortDirection: this.sortDesc ? "D" : "A"
        };

        // Only include optional parameters if they have valid values
        if (this.store?.id) {
            filter.storeId = this.store.id;
        }
        if (this.subStatus !== null && this.subStatus !== undefined) {
            filter.subStatus = this.subStatus;
        }
        if (this.programFilter?.id) {
            filter.programId = this.programFilter.id;
        }

        return filter;
    }

    @Watch("store")
    @Watch("programFilter")
    @Watch("subStatus")
    onFilterChanged() {
        this.persistFilters();
    }

    @Watch("sortBy")
    @Watch("sortDesc")
    resetPageNumber() {
        this.pageNumber = 1;
    }

    patientFormatter(pt: Patient) {
        const pat = Object.assign(new Patient(), pt);
        return pat.displayNamesForPerson();
    }

    async onHoldTableProvider(ctx: BvTableCtxObject) {
        const filter = ctx.filter as any;
        const config: AxiosRequestConfig = {
            params: filter,
        };
        this.loading = true;
        try {
            const resp = await Axios.get<any>(ctx.apiUrl!, config);
            this.totalItemCount = resp.data.count;
            return resp.data.items;
        } catch (err) {
            console.warn(err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }

        return [];
    }

    selectRx(item: Prescription, index?: number, event?: any) {
        const route: any = this.getPrescriptionDetailsRoute(item);
        this.$router.push(route);
    }

    getPrescriptionDetailsRoute(rxRow: Prescription) {
        return {
            name: 'PrescriptionDetails',
            params: {
                storeID: rxRow.storeID,
                rxNumber: rxRow.rxNumber,
                rfNumber: (rxRow.rfNumber || 0)
            }
        };
    }

    private onHoldSubStatuses: Array<{ value: number, label: string, active: boolean }> = [];

    get activeOnHoldSubStatuses() {
        return this.onHoldSubStatuses.filter(ss => ss.active);
    }

    async getOnHoldSubStatuses() {
        try {
            const resp = await this.$http.get("/api/Configuration");
            this.onHoldSubStatuses = resp.data.onHoldSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active
            }));
        } catch {
            //ignore
        }
    }

    subStatusLabel(id: number) {
        return this.onHoldSubStatuses.find(ss => ss.value == id)?.label;
    }

    loadColumnPreferences() {
        const savedPreferences = getUserSetting('onHoldQueueColumnPreferences');
        if (savedPreferences) {
            try {
                const parsed = typeof savedPreferences === 'string' ? JSON.parse(savedPreferences) : savedPreferences;
                this.columnVisibility = {
                    // Core columns defaults
                    productNameShort: true,
                    patient: true,
                    storeName: true,
                    rxId: true,
                    quantity: true,
                    subStatus: true,
                    nextTouchDate: true,
                    fillDate: true,
                    programName: true,
                    prescriberName: true,
                    // Additional columns defaults
                    ndc11: false,
                    daySupply: false,
                    memo: false,
                    directions: false,
                    writtenDate: false,
                    patientPay: false,
                    dispensed: false,
                    // Override with saved preferences
                    ...parsed
                };
            } catch (err) {
                console.error('Failed to load column preferences:', err);
            }
        }
    }

    saveColumnPreferences() {
        try {
            saveUserSetting('onHoldQueueColumnPreferences', JSON.stringify(this.columnVisibility));
            this.$notification(NotificationOptions.notificationOptionsPreset(
                'Column preferences saved successfully',
                NotificationOptions.NotificationTypes.success
            ));
        } catch (err) {
            console.error('Failed to save column preferences:', err);
            this.$notification(NotificationOptions.error('Failed to save column preferences'));
        }
    }
}
</script>

<style scoped lang="scss">
.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 200px), 1fr));
}
</style>
